import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import SearchInput from "@/components/base/SearchInput";
import FilterPanel from "@/components/feature/FilterPanel";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import {
  searchMovies,
  applyMovieFilter,
  defaultFilterState,
  getFeaturedMovies,
} from "@/utils/movie";
import type { MovieFilterState } from "@/utils/movie";
import { useSeo } from "@/hooks/useSeo";

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get("q") ?? "").trim();
  const [filter, setFilter] = useState<MovieFilterState>(defaultFilterState);

  useSeo({
    title: keyword ? `“${keyword}” 的搜索结果 - 韩国三级电影` : "搜索影片 - 韩国三级电影",
    description: keyword
      ? `在韩国三级电影中搜索“${keyword}”的相关作品，支持按类型、年份与评分进一步筛选，快速找到想看的电影。`
      : "在韩国三级电影中按片名、演员或标签搜索作品，快速查找感兴趣的电影。",
    keywords: `韩国三级电影,搜索${keyword ? `,${keyword}` : ""},电影资源,剧情介绍`,
  });

  useEffect(() => {
    setFilter(defaultFilterState);
  }, [keyword]);

  const results = useMemo(() => searchMovies(keyword), [keyword]);
  const filtered = useMemo(() => applyMovieFilter(results, filter), [results, filter]);
  const recommended = useMemo(() => getFeaturedMovies().slice(0, 5), []);

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-7xl px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <header className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <h1 className="text-xl font-black text-foreground-950 md:text-2xl">搜索影片</h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            支持按片名、韩文原名、导演、演员或关键词标签进行模糊匹配。
          </p>
          <div className="mt-4 max-w-2xl">
            <SearchInput key={keyword} size="lg" defaultValue={keyword} />
          </div>
        </header>

        {keyword ? (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm text-foreground-600">
              <span>
                关键词 <strong className="text-primary-600">“{keyword}”</strong>
              </span>
              <span className="text-foreground-400">·</span>
              <span>
                共找到 <strong className="text-foreground-900">{filtered.length}</strong> 部相关作品
              </span>
            </div>

            <div className="mt-4">
              <FilterPanel value={filter} onChange={setFilter} showSort />
            </div>

            {filtered.length > 0 ? (
              <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4 xl:grid-cols-5">
                {filtered.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="mt-6">
                <EmptyState
                  icon="ri-file-search-line"
                  title={`没有找到与“${keyword}”相关的作品`}
                  description="换个关键词试试，或从下方推荐内容开始浏览。"
                  action={
                    <button
                      type="button"
                      onClick={() => setFilter(defaultFilterState)}
                      className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors duration-200 hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className="ri-refresh-line text-base" aria-hidden="true" />
                      重置筛选条件
                    </button>
                  }
                />
              </div>
            )}
          </>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-search-line text-2xl" aria-hidden="true" />
            </div>
            <p className="mt-4 text-base font-bold text-foreground-900">输入关键词开始搜索</p>
            <p className="mt-1.5 text-sm text-foreground-600">
              例如输入「悬疑」「爱情」，或直接搜索你记得的片名。
            </p>
          </div>
        )}

        <section className="mt-10">
          <SectionHeading title="热门推荐" subtitle="不知从何看起？先从这些作品开始" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 md:gap-4 xl:grid-cols-5">
            {recommended.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}